@if (isLoaded) {
  <div class="evaluation-page">
    <!-- Page header -->
    <div class="page-head">
      <div class="page-head-main">
        <div>
          <h1 class="page-title">
            Risk analysis
            @if (optimName) {
              <span class="pill pill-ready">{{ optimName }}</span>
            }
          </h1>
          @if (portfolio) {
            <div class="page-sub">
              {{ riskContributionData?.length || "—" }} assets
              @if (portfolio.start_date && portfolio.end_date) {
                · {{ portfolio.start_date }} → {{ portfolio.end_date }}
              }
            </div>
          }
        </div>
      </div>
      <div class="d-flex align-items-end" style="gap: 8px">
        <button
          class="btn btn-ghost no-print"
          type="button"
          (click)="onExportPdf()"
          [disabled]="!portfolio || !isLoaded"
        >
          Export
        </button>
      </div>
    </div>
    <!-- KPI cards -->
    @if (resultsTableDataFooter[0]) {
      <div
        class="kpi-row mb-3"
        id="risk-expected-risk"
        #expectedRiskReturnChart
      >
        <section class="kpi">
          <span class="label"
            >Expected Return <app-info infoIndex="25"></app-info
          ></span>
          <span class="value">{{
            resultsTableDataFooter[0].expectedReturn
          }}</span>
          @if (baselineFooter) {
            <span
              class="delta"
              [class.pos]="baselineFooter.deltaReturn > 0"
              [class.neg]="baselineFooter.deltaReturn < 0"
            >
              {{ baselineFooter.deltaReturn > 0 ? "▲" : "▼" }}
              {{ baselineFooter.deltaReturn | number: "1.2-2" }}pp vs baseline
            </span>
          }
          @if (!baselineFooter) {
            <span class="delta">Annualized</span>
          }
          <app-sparkline
            class="spark"
            [data]="sparkReturn"
            [width]="90"
            [height]="24"
          ></app-sparkline>
        </section>
        <section class="kpi">
          <span class="label">Expected Volatility</span>
          <span class="value">{{ resultsTableDataFooter[0].volatility }}</span>
          @if (baselineFooter) {
            <span class="delta">
              from {{ baselineFooter.volatility }} baseline
            </span>
          }
          @if (!baselineFooter) {
            <span class="delta">Annualized</span>
          }
          <app-sparkline
            class="spark"
            [data]="sparkVol"
            [width]="90"
            [height]="24"
            color="#FFB547"
          ></app-sparkline>
        </section>
        <section class="kpi">
          <span class="label">Expected Sharpe</span>
          <span class="value">{{ resultsTableDataFooter[0].sharpe }}</span>
          @if (baselineFooter) {
            <span
              class="delta"
              [class.pos]="baselineFooter.deltaSharpe > 0"
              [class.neg]="baselineFooter.deltaSharpe < 0"
            >
              {{ baselineFooter.deltaSharpe > 0 ? "▲" : "▼" }}
              {{ baselineFooter.deltaSharpe | number: "1.2-2" }} vs baseline
            </span>
          }
          @if (!baselineFooter) {
            <span class="delta">Risk-adjusted</span>
          }
          <app-sparkline
            class="spark"
            [data]="sparkReturn"
            [width]="90"
            [height]="24"
            color="#00E5A0"
          ></app-sparkline>
        </section>
        <section class="kpi">
          <span class="label">VaR 95 / VaR 99</span>
          <span class="value neg">{{ var95 * 100 | number: "1.2-2" }}%</span>
          <span class="delta neg"
            >VaR 99: {{ var99 * 100 | number: "1.2-2" }}%</span
          >
          <app-sparkline
            class="spark"
            [data]="sparkDrawdown"
            [width]="90"
            [height]="24"
            color="#FF5C7A"
          ></app-sparkline>
        </section>
      </div>
    }
    <!-- Risk contribution and allocation — self-contained card -->
    <div class="mb-3" #riskReturnContributionChart>
      @if (riskContributionData) {
        <app-risk-contribution-column-chart
          [riskContributionRaw]="riskContributionData"
          [subHeader]="{ portfolio: portfolio.name }"
        >
        </app-risk-contribution-column-chart>
      }
    </div>
    <!-- Marginal Risk Contribution — self-contained card -->
    <div class="mb-3" #summaryMarginalRiskContributionChart>
      @if (marginalRiskContributionData) {
        <app-portfolio-return-bar-chart
          [title]="'Marginal Risk Contribution'"
          [rawData]="marginalRiskContributionData"
        >
        </app-portfolio-return-bar-chart>
      }
    </div>
    <!-- Covariance + Correlation matrices -->
    <div class="row matrix-row">
      <!-- Covariance Matrix -->
      <div
        class="mb-3"
        [class.col-md-6]="!matrixFullWidth"
        [class.col-md-12]="matrixFullWidth"
      >
        <div class="card h-100" #covarianceMatrixTableDataChart>
          <div class="card-body">
            <div class="card-head">
              <div class="card-title">
                Covariance Matrix <span class="hint">· Magnitude</span>
                <app-info infoIndex="27"></app-info>
              </div>
            </div>
            @if (covarianceMatrixTableData) {
              <app-heatmap-table
                [dataRaw]="covarianceMatrixTableData"
                [allowedTickers]="allowedTickers"
              >
              </app-heatmap-table>
            }
          </div>
        </div>
      </div>
      <!-- Correlation Matrix -->
      <div
        class="mb-3"
        [class.col-md-6]="!matrixFullWidth"
        [class.col-md-12]="matrixFullWidth"
      >
        <div class="card h-100" #correlationMatrixTableDataChart>
          <div class="card-body">
            <div class="card-head">
              <div class="card-title">
                Correlation Matrix <span class="hint">· -1 to +1</span>
                <app-info infoIndex="28"></app-info>
              </div>
            </div>
            @if (correlationMatrixTableData) {
              <app-heatmap-table
                [dataRaw]="correlationMatrixTableData"
                [allowedTickers]="allowedTickers"
                [divergent]="true"
              >
              </app-heatmap-table>
            }
          </div>
        </div>
      </div>
    </div>
    <!-- Hot Spots -->
    <div class="card mb-3" #hotSpotsChart>
      <div class="card-body">
        <div class="card-head">
          <div class="card-title">
            Hot Spots <app-info infoIndex="29"></app-info>
          </div>
        </div>
        @if (weightPieData.length > 0) {
          <app-heatmap-chart [dataRaw]="weightPieData"> </app-heatmap-chart>
        }
      </div>
    </div>
    <!-- Drawdown -->
    <div class="card mb-3" #drawdownChart>
      <div class="card-body">
        <div class="card-head">
          <div class="card-title">
            Drawdown <app-info infoIndex="31"></app-info>
          </div>
        </div>
        <app-line-chart
          [series]="comboData"
          [extraOptions]="comboExtraOptions"
        ></app-line-chart>
      </div>
    </div>
    <!-- Volatility -->
    <div class="card mb-3" #volatilityChart>
      <div class="card-body">
        <div class="card-head">
          <div class="card-title">
            Volatility <app-info infoIndex="32"></app-info>
          </div>
        </div>
        <app-line-chart
          [series]="volatilityData.series"
          [extraOptions]="drawDownExtraOptions"
        ></app-line-chart>
      </div>
    </div>
    <!-- Best Hedge — component has its own header + controls -->
    <div class="card mb-4" #bestHedgeCard>
      <div class="card-body">
        <app-best-hedge></app-best-hedge>
      </div>
    </div>
  </div>
}
